import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone, Mail } from "lucide-react";
import api from "../lib/api";
import { useSettings } from "../lib/SettingsContext";

function genSessionId() {
    let s = localStorage.getItem("cc_chat_session");
    if (!s) {
        s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("cc_chat_session", s);
    }
    return s;
}

export default function Chatbot() {
    const { settings } = useSettings();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            text: "Hi! I'm Cake Central's assistant. Tell me about your occasion and I'll help you choose the perfect cake.",
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const sessionId = useRef(genSessionId());
    const listRef = useRef(null);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, open]);

    const send = async () => {
        const text = input.trim();
        if (!text || loading) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text }]);
        setLoading(true);
        try {
            const r = await api.post("/chat", { session_id: sessionId.current, message: text });
            setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
        } catch (e) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    text: "Sorry — I'm having a moment. You can reach our team directly: WhatsApp or call " + settings.phone + ", or email " + settings.email + ".",
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const whatsappLink = `https://wa.me/${(settings.whatsapp || settings.phone || "").replace(/[^\d]/g, "").replace(/^0/, "61")}`;

    return (
        <>
            <button
                onClick={() => setOpen((v) => !v)}
                className="fixed bottom-6 right-6 z-50 bg-blush hover:bg-blush-hover text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition-all"
                aria-label="Open chat"
                data-testid="chatbot-toggle"
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
            </button>

            {open && (
                <div
                    className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] h-[520px] bg-white shadow-2xl flex flex-col animate-fade-in-up"
                    data-testid="chatbot-panel"
                >
                    <header className="px-4 py-3 bg-ink text-white flex items-center justify-between">
                        <div>
                            <p className="font-display text-base">Cake Central Concierge</p>
                            <p className="text-[10px] tracking-[0.2em] uppercase text-white/60">Online now</p>
                        </div>
                        <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" aria-label="Close">
                            <X size={18} />
                        </button>
                    </header>

                    <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-blush-cream">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                data-testid={`chat-msg-${m.role}-${i}`}
                            >
                                <div
                                    className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed ${
                                        m.role === "user"
                                            ? "bg-blush text-white"
                                            : "bg-white text-ink border border-[#EAE3DE]"
                                    }`}
                                >
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start">
                                <div className="bg-white text-ink-soft border border-[#EAE3DE] px-3 py-2 text-sm">
                                    Typing…
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="px-3 py-2 border-t border-[#EAE3DE] flex items-center gap-2 bg-white">
                        <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] tracking-[0.18em] uppercase text-ink hover:text-blush transition-colors"
                            data-testid="chatbot-handoff-whatsapp"
                        >
                            WhatsApp
                        </a>
                        <span className="text-[#EAE3DE]">·</span>
                        <a
                            href={`tel:${settings.phone?.replace(/\s/g, "")}`}
                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-ink hover:text-blush transition-colors"
                            data-testid="chatbot-handoff-phone"
                        >
                            <Phone size={11} /> Call
                        </a>
                        <span className="text-[#EAE3DE]">·</span>
                        <a
                            href={`mailto:${settings.email}`}
                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-ink hover:text-blush transition-colors"
                            data-testid="chatbot-handoff-email"
                        >
                            <Mail size={11} /> Email
                        </a>
                    </div>

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                        className="p-3 border-t border-[#EAE3DE] flex items-center gap-2 bg-white"
                    >
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about a cake, flavour, or your event…"
                            className="flex-1 text-sm px-3 py-2 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                            data-testid="chatbot-input"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-blush hover:bg-blush-hover text-white w-10 h-10 flex items-center justify-center disabled:opacity-50"
                            data-testid="chatbot-send"
                        >
                            <Send size={16} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
